import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GitBranch,
  ArrowRight,
  Link as LinkIcon,
  Unlink,
  Plus,
  Search,
  Loader,
  AlertCircle,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { buildTeamApiService } from '../../services/buildTeamApi';
import type { BuildProjectLink, ProjectLinkType, BuildProject } from '../../types/buildTeam';
import { PROJECT_LINK_TYPES } from '../../types/buildTeam';
import { CreatorProfile } from '../CreatorProfile';
import { usernameCacheService } from '../../services/usernameCache';
import { useDebounce } from '../../hooks/useDebounce';
import { cn } from '../../utils/cn';
import { ConfirmationModal } from '../../components/ConfirmationModal';
import { SectionHeader } from '../ui/SectionHeader';
import { EmptyState } from '../ui/EmptyState';
import { SkeletonLoader } from '../SkeletonLoader';

export interface BuildProjectLinksSectionProps {
  projectId: string;
  canEdit: boolean;
  onUpdate: () => void;
}

function linkedAtMs(ts: number): number {
  return ts > 1e12 ? ts : ts * 1000;
}

const LINK_TYPE_BADGE: Record<ProjectLinkType, string> = {
  related: 'badge-info',
  depends_on: 'badge-amber',
  blocks: 'badge-danger',
};

function formatLinkType(t: ProjectLinkType): string {
  return t.replace(/_/g, ' ');
}

export function BuildProjectLinksSection({ projectId, canEdit, onUpdate }: BuildProjectLinksSectionProps) {
  const navigate = useNavigate();
  const [outgoing, setOutgoing] = useState<BuildProjectLink[]>([]);
  const [incoming, setIncoming] = useState<BuildProjectLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projectTitles, setProjectTitles] = useState<Record<string, string>>({});

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [searchResults, setSearchResults] = useState<BuildProject[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
  const [linkType, setLinkType] = useState<ProjectLinkType>('related');
  const [linkSubmitting, setLinkSubmitting] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [unlinkingKey, setUnlinkingKey] = useState<string | null>(null);
  const [confirmAction, setConfirmAction] = useState<{type: 'outgoing' | 'incoming'; id: string} | null>(null);

  const loadLinks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await buildTeamApiService.getLinkedProjects(projectId);
      setOutgoing(data.outgoing ?? []);
      setIncoming(data.incoming ?? []);
      const uuids = new Set<string>();
      [...(data.outgoing ?? []), ...(data.incoming ?? [])].forEach((l) => {
        if (l.linked_by) uuids.add(l.linked_by);
      });
      if (uuids.size > 0) usernameCacheService.prefetchUsernames(Array.from(uuids));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load project links');
      setOutgoing([]);
      setIncoming([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  useEffect(() => {
    const ids = new Set<string>();
    outgoing.forEach((l) => ids.add(l.target_project_id));
    incoming.forEach((l) => ids.add(l.source_project_id));
    ids.delete(projectId);
    const idList = Array.from(ids);
    if (idList.length === 0) {
      setProjectTitles({});
      return;
    }
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        idList.map(async (id) => {
          try {
            const p = await buildTeamApiService.getProjectById(id);
            return [id, p.title] as const;
          } catch {
            return [id, id] as const;
          }
        })
      );
      if (!cancelled) {
        setProjectTitles(Object.fromEntries(entries));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, outgoing, incoming]);

  useEffect(() => {
    if (!showAddForm || debouncedSearch.length < 2) {
      setSearchResults([]);
      return;
    }
    let cancelled = false;
    setSearchLoading(true);
    buildTeamApiService
      .searchProjects(debouncedSearch, 1, 20)
      .then((results) => {
        if (cancelled) return;
        const filtered = results.filter((p) => p.id !== projectId);
        setSearchResults(filtered);
      })
      .catch(() => {
        if (!cancelled) setSearchResults([]);
      })
      .finally(() => {
        if (!cancelled) setSearchLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, showAddForm, projectId]);

  const goToProject = (id: string) => {
    navigate(`/build-team/projects/${id}`);
  };

  const handleUnlinkOutgoing = async (targetId: string) => {
    if (!canEdit || unlinkingKey) return;
    setConfirmAction({ type: 'outgoing', id: targetId });
  };

  const handleUnlinkIncoming = async (sourceId: string) => {
    if (!canEdit || unlinkingKey) return;
    setConfirmAction({ type: 'incoming', id: sourceId });
  };

  const executeUnlink = async () => {
    if (!confirmAction) return;
    const { type, id } = confirmAction;
    const key = type === 'outgoing' ? `out:${id}` : `in:${id}`;
    setUnlinkingKey(key);
    setConfirmAction(null);
    try {
      if (type === 'outgoing') {
        await buildTeamApiService.unlinkProjects(projectId, id);
      } else {
        await buildTeamApiService.unlinkProjects(id, projectId);
      }
      await loadLinks();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove link');
    } finally {
      setUnlinkingKey(null);
    }
  };

  const handleAddLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canEdit || !selectedTargetId || linkSubmitting) return;
    setLinkSubmitting(true);
    try {
      await buildTeamApiService.linkProjects(projectId, selectedTargetId, linkType);
      setSearchQuery('');
      setSelectedTargetId(null);
      setShowAddForm(false);
      await loadLinks();
      onUpdate();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create link');
    } finally {
      setLinkSubmitting(false);
    }
  };

  const renderLinkRow = (
    otherId: string,
    link: BuildProjectLink,
    direction: 'out' | 'in',
    onUnlink: () => void
  ) => {
    const title = projectTitles[otherId] ?? otherId;
    const unlinkKey = direction === 'out' ? `out:${otherId}` : `in:${otherId}`;
    return (
      <div
        key={`${direction}-${otherId}-${link.linked_at}`}
        className="subcard p-3 flex flex-col sm:flex-row sm:items-center gap-3"
      >
        <div className="flex items-start gap-2 flex-1 min-w-0">
          {direction === 'out' ? (
            <ArrowRight className="w-4 h-4 text-glass-500 flex-shrink-0 mt-0.5" />
          ) : (
            <LinkIcon className="w-4 h-4 text-glass-500 flex-shrink-0 mt-0.5" />
          )}
          <div className="min-w-0 flex-1">
            <button
              type="button"
              onClick={() => goToProject(otherId)}
              className="text-left text-sm font-medium text-white hover:text-accent-300 hover:underline underline-offset-2 truncate w-full"
            >
              {title}
            </button>
            <div className="flex flex-wrap items-center gap-2 mt-1">
              <span
                className={cn('badge capitalize', LINK_TYPE_BADGE[link.link_type])}
              >
                {formatLinkType(link.link_type)}
              </span>
              <span className="text-xs text-glass-500 tabular-nums">
                {formatDistanceToNow(new Date(linkedAtMs(link.linked_at)), { addSuffix: true })}
              </span>
            </div>
            <p className="text-xs text-glass-400 mt-1">
              Linked by <CreatorProfile creatorId={link.linked_by} inline />
            </p>
          </div>
        </div>
        {canEdit && (
          <button
            type="button"
            onClick={onUnlink}
            disabled={unlinkingKey === unlinkKey}
            className="glass-button-sm text-red-400 hover:text-red-300 self-start sm:self-center touch-manipulation"
          >
            {unlinkingKey === unlinkKey ? (
              <Loader className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Unlink className="w-3.5 h-3.5" />
            )}
            Remove
          </button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <SkeletonLoader className="h-4 w-32" />
          <SkeletonLoader className="h-9 w-28" />
        </div>
        <SkeletonLoader className="h-3 w-24" />
        <div className="space-y-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <SkeletonLoader key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
        <SkeletonLoader className="h-3 w-24" />
        <SkeletonLoader className="h-16 w-full rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        bare
        compact
        tone="danger"
        icon={AlertCircle}
        title="Failed to load project links"
        description={error}
        action={
          <button type="button" onClick={loadLinks} className="glass-button">
            Retry
          </button>
        }
      />
    );
  }

  return (
    <div className="space-y-4">
      <SectionHeader
        title="Project Links"
        icon={GitBranch}
        count={outgoing.length + incoming.length}
        className="mb-0"
        actions={
          canEdit ? (
            <button
              type="button"
              onClick={() => setShowAddForm((v) => !v)}
              className="glass-button"
            >
              <Plus className="w-4 h-4" />
              Add link
            </button>
          ) : undefined
        }
      />

      {showAddForm && canEdit && (
        <form
          onSubmit={handleAddLink}
          className="subcard p-4 space-y-3"
        >
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="glass-input w-full pl-9"
              placeholder="Search projects (min 2 characters)…"
              autoFocus
            />
            {searchLoading && (
              <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-glass-400" />
            )}
          </div>
          {searchQuery.length > 0 && searchQuery.length < 2 && (
            <p className="text-xs text-glass-500">Type at least 2 characters…</p>
          )}
          {debouncedSearch.length >= 2 && !searchLoading && searchResults.length === 0 && (
            <p className="text-xs text-glass-500">No projects found</p>
          )}
          {searchResults.length > 0 && (
            <div className="max-h-40 overflow-y-auto rounded-lg border border-glass-200/10 divide-y divide-glass-200/10">
              {searchResults.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSelectedTargetId(p.id)}
                  className={cn(
                    'w-full text-left px-3 py-2 text-sm transition-colors',
                    selectedTargetId === p.id ? 'bg-accent-500/15 text-white' : 'hover:bg-glass-200/5 text-glass-200'
                  )}
                >
                  <span className="font-medium truncate block">{p.title}</span>
                  <span className="text-[10px] text-glass-500 font-mono">{p.id.slice(0, 8)}…</span>
                </button>
              ))}
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-glass-400 mb-1.5 block">Link type</label>
            <select
              value={linkType}
              onChange={(e) => setLinkType(e.target.value as ProjectLinkType)}
              className="glass-select w-full capitalize"
            >
              {PROJECT_LINK_TYPES.map((t) => (
                <option key={t} value={t}>
                  {formatLinkType(t)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setShowAddForm(false)} className="glass-button-secondary">
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedTargetId || linkSubmitting}
              className="btn-primary"
            >
              {linkSubmitting ? <Loader className="w-4 h-4 animate-spin" /> : <LinkIcon className="w-4 h-4" />}
              Link project
            </button>
          </div>
        </form>
      )}

      <div className="space-y-4">
        <div>
          <h4 className="eyebrow mb-2 flex items-center gap-1.5">
            <ArrowRight className="w-3.5 h-3.5" />
            Outgoing
            <span className="tabular-nums">({outgoing.length})</span>
          </h4>
          {outgoing.length === 0 ? (
            <p className="text-xs text-glass-500 py-3 text-center rounded-lg border border-dashed border-glass-200/15">
              No outgoing links
            </p>
          ) : (
            <div className="space-y-2">
              {outgoing.map((link) =>
                renderLinkRow(link.target_project_id, link, 'out', () =>
                  handleUnlinkOutgoing(link.target_project_id)
                )
              )}
            </div>
          )}
        </div>

        <div>
          <h4 className="eyebrow mb-2 flex items-center gap-1.5">
            <LinkIcon className="w-3.5 h-3.5" />
            Incoming
            <span className="tabular-nums">({incoming.length})</span>
          </h4>
          {incoming.length === 0 ? (
            <p className="text-xs text-glass-500 py-3 text-center rounded-lg border border-dashed border-glass-200/15">
              No incoming links
            </p>
          ) : (
            <div className="space-y-2">
              {incoming.map((link) =>
                renderLinkRow(link.source_project_id, link, 'in', () =>
                  handleUnlinkIncoming(link.source_project_id)
                )
              )}
            </div>
          )}
        </div>
      </div>

      {confirmAction && (
        <ConfirmationModal
          isOpen
          onClose={() => setConfirmAction(null)}
          onConfirm={executeUnlink}
          title={confirmAction.type === 'outgoing' ? 'Remove Project Link' : 'Remove Incoming Link'}
          message={confirmAction.type === 'outgoing'
            ? 'Are you sure you want to remove this project link?'
            : 'Are you sure you want to remove this incoming link?'}
          confirmText="Remove"
          isLoading={!!unlinkingKey}
        />
      )}
    </div>
  );
}
